/*
 * Status badge — single component encoding the Phase 6 spec'd statuses
 * with consistent colours so the same word means the same thing every
 * time it appears (table cell, dashboard card, drawer).
 *
 * If a status string isn't recognised, falls back to a neutral pill
 * with the raw text — no thrown errors, no empty pills.
 */

import type { ReactNode } from "react";

export type StatusKind =
  | "healthy" | "warning" | "error"
  | "running" | "queued" | "completed" | "failed" | "succeeded"
  | "paused" | "blocked" | "not_configured" | "subscription_blocked"
  | "draft" | "published" | "active" | "inactive" | "pending"
  | "dead-lettered" | "abandoned" | "delivered";

const TONE_BY_STATUS: Partial<Record<StatusKind, "ok" | "warn" | "err" | "info" | "muted">> = {
  healthy: "ok",       active: "ok",       completed: "ok",     succeeded: "ok",     published: "ok", delivered: "ok",
  warning: "warn",     paused: "warn",     pending: "warn",
  error: "err",        failed: "err",      "dead-lettered": "err", abandoned: "err",  blocked: "err", subscription_blocked: "err",
  running: "info",     queued: "info",
  draft: "muted",      inactive: "muted",  not_configured: "muted"
};

export interface StatusBadgeProps {
  status: string;
  children?: ReactNode;
  /** Override the inferred tone — useful when context narrows the meaning. */
  tone?: "ok" | "warn" | "err" | "info" | "muted";
  title?: string;
}

export default function StatusBadge({ status, children, tone, title }: StatusBadgeProps) {
  const inferred = tone ?? TONE_BY_STATUS[status as StatusKind] ?? "muted";
  return (
    <span className={`pill pill-${inferred}`} title={title} data-status={status}>
      {children ?? status}
    </span>
  );
}
